/**
 * ContratsIndexation — Hub indexation : INSEE fetch + Simulateur what-if
 * + Liste indexés + Batch renégocier.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  TrendingUp, TrendingDown, RefreshCw, Loader2, Sparkles, Download,
  FileText, ArrowRight, Calendar, Zap, Info,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";

function SimulateurDialog({ open, onOpenChange, currentIndices }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open && currentIndices) {
      const init = {};
      Object.entries(currentIndices).forEach(([k, v]) => {
        init[k] = { latest: v.latest, prior: v.prior };
      });
      setValues(init);
    }
  }, [open, currentIndices]);

  const runSim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contracts/indexation-simulate", {
        simulated_indices: values, window_days: 365,
      });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="simulator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            Simulateur d'impact — Simulation
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <div className="text-xs text-slate-500">
            Modifie une ou plusieurs valeurs d'indice pour voir la dérive
            €/an prévisionnelle sur ton portefeuille.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(values).map(([k, v]) => (
            <Card key={k} className="p-3">
              <div className="text-xs font-medium mb-2">{k}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Actuel</Label>
                  <Input type="number" step="0.01" value={v.latest}
                          onChange={(e) => setValues({ ...values,
                            [k]: { ...v, latest: parseFloat(e.target.value) } })}
                          className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Précédent</Label>
                  <Input type="number" step="0.01" value={v.prior}
                          onChange={(e) => setValues({ ...values,
                            [k]: { ...v, prior: parseFloat(e.target.value) } })}
                          className="h-8 text-xs" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Button onClick={runSim} disabled={busy}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                data-testid="simulator-run">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Zap className="h-4 w-4 mr-1.5" />}
          Simuler l'impact
        </Button>

        {preview && (
          <Card className="p-4 bg-purple-50/40 border-purple-200 mt-2">
            <div className="text-xs uppercase tracking-widest text-purple-700 mb-1">
              Impact simulé
            </div>
            <div className="text-2xl font-bold text-purple-900 tabular-nums">
              {preview.total_drift_year_ht > 0 ? "+" : ""}
              {fmtEUR(preview.total_drift_year_ht)}/an
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {preview.count} contrat(s) impacté(s)
            </div>
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function ContratsIndexation({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showSim, setShowSim] = useState(false);
  const [genning, setGenning] = useState(false);

  const load = async () => {
    setData(null);
    try {
      const [imp, cfg] = await Promise.all([
        api.get("/contracts/indexation-impact?window_days=365"),
        api.get("/contracts/indexation-config"),
      ]);
      setData(imp.data);
      setConfig(cfg.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const fetchInsee = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/contracts/indexation-fetch-insee");
      toast.success(`INSEE actualisé : ${(data.fetched || []).join(", ")}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setFetching(false); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const batchRenegotiate = async () => {
    if (selected.size === 0) return;
    setGenning(true);
    try {
      const r = await api.post("/contracts/batch-renegotiate",
        { contract_ids: Array.from(selected), kind: "non_reconduction" },
        { responseType: "blob", timeout: 300000 });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lettres renegociation ${selected.size} contrats.zip`;
      a.click();
      URL.revokeObjectURL(url);
      const gen = r.headers["x-report-generated"] || "?";
      toast.success(`${gen} lettres générées et zippées`);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenning(false); }
  };

  if (!data || !config) return (
    <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
  );

  return (
    <div className="space-y-5" data-testid="indexation-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Indexation
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-3xl">
            Suivi des indices INSEE (ILC, ILAT, IPC) + simulation d'impact +
            renégociation en masse en 1 clic. La dérive €/an est calculée
            automatiquement pour chaque contrat indexé.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchInsee} disabled={fetching}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="insee-fetch-btn">
            {fetching ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Actualiser INSEE
          </Button>
          <Button variant="outline" onClick={() => setShowSim(true)}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="simulator-open-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Simulateur d'impact
          </Button>
        </div>
      </div>

      {/* Indices actuels */}
      <Card className="p-4">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
          Indices actuels
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(config.indices || {}).map(([k, v]) => (
            <div key={k} className="p-2 rounded border border-slate-200 bg-slate-50/50">
              <div className="text-xs font-semibold">{k}</div>
              <div className="text-lg font-mono tabular-nums">{v.latest}</div>
              <div className="text-[10px] text-slate-500">
                {v.period} · préc. {v.prior}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* KPI drift */}
      <Card className={cn("p-5", data.total_drift_year_ht >= 0
                                   ? "bg-red-50/30 border-red-200"
                                   : "bg-emerald-50/30 border-emerald-200")}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Dérive portefeuille estimée</div>
            <div className={cn("text-4xl font-bold tabular-nums mt-1",
              data.total_drift_year_ht >= 0 ? "text-red-700" : "text-emerald-700")}>
              {data.total_drift_year_ht >= 0 ? "+" : ""}{fmtEUR(data.total_drift_year_ht)}/an
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {data.count} contrat(s) impacté(s) · {data.covered_contracts} indexé(s) au total
            </div>
          </div>
          {data.total_drift_year_ht >= 0
            ? <TrendingUp className="h-12 w-12 text-red-500" />
            : <TrendingDown className="h-12 w-12 text-emerald-500" />}
        </div>
      </Card>

      {/* Batch renegotiate bar */}
      {selected.size > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-300 sticky top-16 z-10 flex items-center justify-between gap-3"
              data-testid="batch-bar">
          <div className="text-sm font-medium">
            {selected.size} contrat(s) sélectionné(s)
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Effacer
            </Button>
            <Button size="sm" onClick={batchRenegotiate} disabled={genning}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="batch-renegotiate-btn">
              {genning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                       : <Download className="h-3.5 w-3.5 mr-1.5" />}
              Générer {selected.size} lettre(s) (ZIP)
            </Button>
          </div>
        </Card>
      )}

      {/* Contracts list */}
      <Card>
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold">Contrats indexés impactés ({data.count})</h3>
        </div>
        {data.items?.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Aucun contrat impacté dans la fenêtre analysée.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.items.map((it) => (
              <label key={it.contract_id}
                className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                data-testid={`indexed-${it.contract_id}`}>
                <Checkbox checked={selected.has(it.contract_id)}
                          onCheckedChange={() => toggleSelect(it.contract_id)}
                          data-testid={`select-${it.contract_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {it.title || it.party_name}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{it.index_name}</Badge>
                    <span>{it.party_name}</span>
                    <span>·</span>
                    <span><Calendar className="h-2.5 w-2.5 inline mr-0.5" />
                      Rev {fmtDate(it.next_revision_date)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={cn("text-sm font-mono font-semibold tabular-nums",
                    it.drift_year_ht > 0 ? "text-red-700" : "text-emerald-700")}>
                    {it.drift_year_ht > 0 ? "+" : ""}{fmtEUR(it.drift_year_ht)}/an
                  </div>
                  <div className="text-[9px] text-slate-500 tabular-nums">
                    {it.drift_pct > 0 ? "+" : ""}{it.drift_pct}%
                  </div>
                </div>
                <button onClick={(e) => { e.preventDefault(); navigate(`/app/contracts/${it.contract_id}`); }}
                        className="text-slate-400 hover:text-blue-600 p-1">
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </label>
            ))}
          </div>
        )}
      </Card>

      <div className="text-[11px] text-slate-500 flex items-start gap-2 border-t pt-3">
        <Info className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          Les lettres en masse utilisent le format "non_reconduction" par défaut.
          Chaque DOCX est adapté au contrat (parties, référence, date effet).
          Le digest hebdomadaire des anomalies est envoyé chaque lundi 7h UTC.
        </span>
      </div>

      <SimulateurDialog open={showSim} onOpenChange={setShowSim}
                          currentIndices={config?.indices} />
    </div>
  );
}
